import { create } from 'zustand';
import type { ActionTypeId } from '../constants/achievementBadges';

/**
 * The one blocked create attempt currently being explained to the user.
 *
 * ⚠️ DELIBERATELY NOT PERSISTED and deliberately not synced. This is transient
 * UI state about a click that just happened; a block restored on reload would
 * be a modal the user never asked for, about a create they have long forgotten.
 * Every other store in `stores/` rides a cloud blob — this one must not.
 */
export interface CapacityBlock {
  type: ActionTypeId;
  domainId: string;
  /** Quest id for a goal, ability id for a skill. */
  parentId?: string;
  /**
   * How many the blocked attempt tried to create. Absent or 1 = an ordinary
   * single create.
   *
   * ⚠️⚠️ THE MODAL'S HEADLINE DEPENDS ON THIS. A batch can be refused while the
   * domain is NOT full — 2 of 5 used, 8 requested — and saying "at capacity"
   * over a "2 of 5" reading would be a plain contradiction on screen. Anything
   * above 1 gets different copy.
   */
  adding?: number;
  /**
   * What the user was typing when they were stopped, if the create path had it.
   *
   * ⭐ This is what makes "put it in the Inbox instead" an actual continuation
   * rather than a consolation prize — the thought they were capturing survives
   * the block. Paths with no title yet simply omit it.
   */
  draftTitle?: string;
  draftDescription?: string;
}

interface CapacityBlockState {
  block: CapacityBlock | null;
  openCapacityBlock: (block: CapacityBlock) => void;
  closeCapacityBlock: () => void;
}

export const useCapacityBlockStore = create<CapacityBlockState>()((set) => ({
  block: null,
  openCapacityBlock: (block) => set({ block }),
  closeCapacityBlock: () => set({ block: null }),
}));
